import CloseIcon from "@mui/icons-material/Close";
import { observer } from "mobx-react-lite";
import useClosable from "../../hooks/useClosable";
import IProperty from "../../interfaces/IProperty";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import Modal from "../Modal";
import styles from "./ZoningDetailsModal.module.css";

function ZoningDetailsModal({ property, onClose }: IZoningDetailsModalProps) {
    useClosable(ClosableOrder.High, onClose);

    return (
        <Modal header="Zoning Details" onClose={onClose} midWidth>
            <CloseIcon className={styles.closeIcon} onClick={onClose} />
            <div className={styles.scrollable}>
                <div className={styles.fields}>
                    {(property.zoning || property.zoningDescription) && (
                        <div className={commonStyles.row}>
                            <div className={commonStyles.key}>Zoning Name</div>
                            <div className={commonStyles.value}>
                                {property.zoning}{property.zoningDescription && ` (${property.zoningDescription})`}
                            </div>
                        </div>
                    )}
                    {property.zoningSubtype && (
                        <div className={commonStyles.row}>
                            <div className={commonStyles.key}>Type</div>
                            <div className={commonStyles.value}>{property.zoningSubtype}</div>
                        </div>
                    )}
                    {property.zoningCodeLink && (
                        <div className={commonStyles.row}>
                            <div className={commonStyles.key}>Code Link</div>
                            <div className={commonStyles.value}>
                                <a href={property.zoningCodeLink} target="_blank" rel="noreferrer" className={commonStyles.link}>
                                    View Code
                                </a>
                            </div>
                        </div>
                    )}
                </div>
                {property.zoningObjective && (
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>Objective</div>
                        <p className={styles.objective}>{property.zoningObjective}</p>
                    </div>
                )}
                {property.permittedLandUses && <LandUseSection title="Permitted Uses" json={property.permittedLandUses} />}
                {property.permittedLandUsesAsOfRight && <LandUseSection title="By-right Uses" json={property.permittedLandUsesAsOfRight} />}
                {property.permittedLandUsesConditional && <LandUseSection title="Conditional Uses" json={property.permittedLandUsesConditional} />}
            </div>
        </Modal>
    );
}

function LandUseSection({ title, json }: { title: string; json: string }) {
    let data: Record<string, string[]>;
    try {
        data = JSON.parse(json);
    } catch {
        return null;
    }

    const categories = Object.entries(data);
    if (!categories.length) return null;

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>{title}</div>
            {categories.map(([category, uses]) => (
                <div key={category} className={styles.category}>
                    <div className={styles.categoryTitle}>{toTitleCase(category)}</div>
                    <ul className={styles.usesList}>
                        {uses.map((use, i) => (
                            <li key={i}>{toTitleCase(use)}</li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function toTitleCase(str: string): string {
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

interface IZoningDetailsModalProps {
    property: IProperty;
    onClose: () => void;
}

export default observer(ZoningDetailsModal);
